import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, Star, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLORS = {
  common: { bg: '#F3F4F6', text: '#6B7280', label: 'Common' },
  rare: { bg: '#DBEAFE', text: '#2563EB', label: 'Rare' },
  epic: { bg: '#EDE9FE', text: '#7C3AED', label: 'Epic' },
  legendary: { bg: '#FEF3C7', text: '#D97706', label: 'Legendary' },
};

const CollectibleShowcase = () => {
  const { slug } = useParams();
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/engagement/showcase/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(setShowcase)
      .catch(() => setError('Showcase not found or is private.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 flex items-center justify-center">
      <div className="text-white/50 text-sm">Loading showcase...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 flex items-center justify-center">
      <div className="text-center">
        <Trophy size={40} className="text-white/20 mx-auto mb-4" />
        <p className="text-white/50 text-sm">{error}</p>
        <a href="/pre-launch" className="text-purple-400 text-xs underline mt-3 inline-block">Join HiBFF</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 text-white" data-testid="showcase-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl font-bold mb-4">
            {showcase.display_name?.charAt(0) || '?'}
          </div>
          <h1 className="text-2xl font-bold">{showcase.display_name}&apos;s Collection</h1>
          {showcase.bio && <p className="text-sm text-white/50 mt-2 max-w-md mx-auto">{showcase.bio}</p>}
          <p className="text-xs text-white/30 mt-3">{showcase.total} collectible{showcase.total !== 1 ? 's' : ''}</p>
        </div>

        {/* Grid */}
        {showcase.collectibles?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {showcase.collectibles.map(c => {
              const rarity = RARITY_COLORS[c.rarity] || RARITY_COLORS.common;
              return (
                <div key={c.collectible_id} className="rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-all group">
                  <div className="aspect-[3/4] bg-white/5 overflow-hidden">
                    {c.image_display_url ? (
                      <img src={c.image_display_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Star size={24} className="text-white/20" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-semibold text-white truncate">{c.title || 'Collectible'}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: rarity.bg, color: rarity.text }}>
                        {rarity.label}
                      </span>
                      <span className="text-[9px] text-white/30">{c.type}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/30 text-sm">This collection is just getting started.</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <p className="text-xs text-white/20">Powered by HiBFF</p>
          <a href="/pre-launch" className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 mt-2">
            Start your own collection <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CollectibleShowcase;
